import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../ui/Button';
import { modeConfirm } from '../monitorsView';
import { useMonitorUpdate } from './useMonitorUpdate';
import type { MonitorSettingsRowData } from './types';

/**
 * Testing ↔ Live for one monitor (spec §3, §7), ASKED before it moves anything: the open
 * alerts move with the switch in the same save, so the step says how many become visible on
 * the board (or leave it) before the owner confirms. Nobody is notified by the switch.
 *
 * The confirm step opens under its trigger and takes the focus (its question first, so a
 * screen reader hears what is being asked); Cancel and a landed switch hand it back to the
 * trigger, never to <body>. Drawn in a monitor's row and at the foot of its testing review:
 * the same control and the same words in both. Where a landed switch takes the trigger away
 * (the review of a monitor that is now Live), `onLanded` says where the focus goes instead.
 */
export function ModeSwitch({
  row,
  onLanded,
}: {
  row: MonitorSettingsRowData;
  onLanded?: () => void;
}) {
  const update = useMonitorUpdate();
  const [asking, setAsking] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const step = useRef<HTMLDivElement>(null);
  const stepId = useId();
  const to = row.mode === 'live' ? 'testing' : 'live';
  const words = modeConfirm(row.label, to, row.open);

  useEffect(() => {
    if (asking) step.current?.focus();
  }, [asking]);

  const close = () => {
    setAsking(false);
    update.clearError();
    trigger.current?.focus();
  };

  // `contents`: the trigger sits in its row's line of controls, and the step below takes a
  // whole line of its own (`basis-full`), so opening it never moves the trigger.
  return (
    <div className="contents">
      <Button
        ref={trigger}
        variant="ghost"
        size="sm"
        className="min-h-9"
        aria-expanded={asking}
        aria-controls={asking ? stepId : undefined}
        onClick={() => (asking ? close() : setAsking(true))}
      >
        {to === 'live' ? 'Switch to Live…' : 'Back to Testing…'}
      </Button>
      {asking ? (
        <div
          id={stepId}
          ref={step}
          tabIndex={-1}
          role="group"
          aria-label={words.question}
          className="min-w-0 basis-full rounded-[var(--radius-sm)] border border-border bg-surface-2 p-3 text-sm"
          data-mode-confirm={to}
        >
          <p className="font-semibold">{words.question}</p>
          <p className="mt-1 text-muted-foreground">{words.consequence}</p>
          {update.error ? (
            <p role="alert" className="mt-2 text-danger">
              {update.error}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              size="sm"
              className="min-h-9"
              aria-disabled={update.busy || undefined}
              onClick={() => {
                if (update.busy) return;
                update.send({ key: row.key, mode: to }, () => {
                  if (!onLanded) return close();
                  setAsking(false);
                  onLanded();
                });
              }}
            >
              {words.action}
            </Button>
            <Button variant="ghost" size="sm" className="min-h-9" onClick={close}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
